// Carrying the copy-link secret across the auth round trip. No React, no fetch
// (lib/ convention). Sibling of ./url.js, which parses the fragment and scrubs the
// address bar; this file holds the parsed value for the length of one login
// redirect. url.js's own header asks for exactly this split — stashing is storage
// I/O with a lifetime, not string parsing.
//
// WHY IT HAS TO EXIST. The secret rides in the URL fragment because fragments are
// never transmitted (§5.1). Redeem needs a session, and an unauthenticated arrival
// is bounced to Zitadel by lib/apiClient's 401 handler, which rebuilds the return
// URL from `pathname + search` (apiClient.js:16) — no fragment, and a full-page
// OIDC round trip would discard one anyway. So between the arrival and the redeem
// there is a navigation the fragment does not survive, and the secret has to live
// somewhere same-origin, per-tab, and not in a URL. `sessionStorage` is that
// place: scoped to the origin AND to the tab, so a second tab driving its own
// redemption has its own storage and cannot read or clobber this one. (A tab
// DUPLICATED mid-flow inherits a copy of the entry — harmless: same user, and
// redeem is idempotent per `(copy_id, recipient_user_id)`.)
//
// READING IS NOT TAKING, and this is the trap worth writing down. The obvious
// design — a `take()` that removes as it reads — destroys the very case the stash
// exists for: the value is read, POSTed, the POST 401s, the page leaves for the
// IdP, and on return the entry is gone, so the recipient can never redeem. The
// entry is therefore removed only by an explicit `clearStashedSecret` on a
// TERMINAL outcome (redeemed, or a refusal that cannot become a success), never by
// the read.
//
// Nothing here throws. `sessionStorage` access itself throws in a sandboxed frame,
// and `setItem` throws in Safari private mode and on a full quota — a recipient
// whose browser refuses storage must still be able to redeem in one shot, so every
// entry point is total and reports failure by return value.

import { clearFragment, readFragmentSecret } from "./url.js";
import { defaultSessionStorage } from "../util/sessionStorage.js";

/**
 * How long a stashed secret stays usable. Sized for one interactive login — a
 * password plus an MFA prompt or a passkey enrolment — and no longer: the entry is
 * a live capability at rest, so a value that lingered for the whole browser
 * session would outlive any plausible round trip.
 */
export const STASH_TTL_MS = 10 * 60 * 1000;

/** Namespaced per copy id, so two links opened in one tab cannot collide. */
export function stashKey(copyId) {
  return `soothCopySecret:${copyId}`;
}

/**
 * Park a secret for the auth round trip.
 *
 * @param {string} copyId
 * @param {string} secret  a validated base64url secret (see readFragmentSecret)
 * @param {{storage?: object|null, now?: number}} [opts]
 * @returns {boolean} true only if the value is actually retrievable afterwards.
 *   The caller uses this to decide whether it is safe to scrub the fragment —
 *   see acquireCopySecret.
 */
export function stashSecret(copyId, secret, { storage, now = Date.now() } = {}) {
  const store = storage === undefined ? defaultSessionStorage() : storage;
  if (!store || !copyId || typeof secret !== "string" || !secret) return false;
  try {
    store.setItem(stashKey(copyId), JSON.stringify({ s: secret, e: now + STASH_TTL_MS }));
    return true;
  } catch {
    return false;
  }
}

/**
 * Read a previously stashed secret, or null.
 *
 * Anything unusable — absent, corrupt, expired, or not shaped like a mint secret —
 * is REMOVED as it is rejected, so a bad entry cannot sit there being re-read on
 * every reload.
 *
 * The alphabet check runs the stored value back through `readFragmentSecret` and
 * demands an IDENTICAL string, rather than re-declaring the base64url pattern
 * here. Two reasons: the capsule's rule about hand-kept duplicates is easier to
 * honour by not creating one, and the identity comparison additionally rejects a
 * value `readFragmentSecret` would have *normalised* (a stored `"#abc"` parses to
 * `"abc"` — a shape nothing legitimate writes, so it is a bug, not input).
 *
 * @param {string} copyId
 * @param {{storage?: object|null, now?: number}} [opts]
 * @returns {string|null}
 */
export function readStashedSecret(copyId, { storage, now = Date.now() } = {}) {
  const store = storage === undefined ? defaultSessionStorage() : storage;
  if (!store || !copyId) return null;
  let raw;
  try {
    raw = store.getItem(stashKey(copyId));
  } catch {
    return null;
  }
  if (typeof raw !== "string" || !raw) return null;

  const drop = () => {
    clearStashedSecret(copyId, { storage: store });
    return null;
  };

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return drop();
  }
  if (!parsed || typeof parsed !== "object") return drop();
  if (typeof parsed.s !== "string" || !Number.isFinite(parsed.e)) return drop();
  if (now >= parsed.e) return drop();
  if (readFragmentSecret(parsed.s) !== parsed.s) return drop();
  return parsed.s;
}

/**
 * Remove the entry. Called on a terminal outcome only — see the header.
 *
 * @param {string} copyId
 * @param {{storage?: object|null}} [opts]
 * @returns {void}
 */
export function clearStashedSecret(copyId, { storage } = {}) {
  const store = storage === undefined ? defaultSessionStorage() : storage;
  if (!store || !copyId) return;
  try {
    store.removeItem(stashKey(copyId));
  } catch {
    /* a store that refuses removeItem is a store we never wrote to */
  }
}

/**
 * Get the secret for one redemption attempt: the fragment if this is the arrival,
 * otherwise whatever survived the login round trip.
 *
 * THE ORDER IS THE INVARIANT. Stash first, scrub second, and scrub ONLY if the
 * stash succeeded. Scrubbing an unstashed secret is unrecoverable — the fragment
 * is the only copy that exists (the server keeps a hash), so a 401 arriving after
 * a failed stash would strand the recipient with a link they can no longer use.
 * Leaving the fragment on screen when storage is unavailable is the lesser harm:
 * it is a shoulder-surfing/screenshot exposure, not a transmission, and the caller
 * scrubs it once the redemption reaches a terminal state.
 *
 * @param {string} copyId
 * @param {{win?: object|null, storage?: object|null, now?: number}} [opts]
 * @returns {string|null} the secret to POST, or null when there is none anywhere
 */
export function acquireCopySecret(copyId, { win, storage, now = Date.now() } = {}) {
  const fromFragment = readFragmentSecret(win && win.location ? win.location.hash : null);
  if (fromFragment) {
    if (stashSecret(copyId, fromFragment, { storage, now })) clearFragment(win);
    return fromFragment;
  }
  return readStashedSecret(copyId, { storage, now });
}
